import { Dropdown, Field, Input, makeStyles, mergeClasses, Option } from '@fluentui/react-components';
import { DarkMode, GridView, Lightbulb, LightMode, ViewList, Visibility, VisibilityOff } from '@material-symbols-svg/react/rounded/w500';
import { useState } from 'react';

import { SectionLabel } from '../../components';
import { type ThemeMode, usePreferences, type ViewMode } from '../../preferences';
import { palette, tokens, useSurfaceStyles } from '../../theme';
import { ORG_OPTIONS, PROJECT_OPTIONS, Settings } from '../models';
import { useSettings } from '../SettingsProvider';

export const SettingsView: React.FC = () => {
    const classes = useStyles();
    const surfaces = useSurfaceStyles();
    const { settings, save } = useSettings();
    const { preferences, setDefaultView, setThemeMode } = usePreferences();

    const [patVisible, setPatVisible] = useState(false);

    const update = <K extends keyof Settings>(key: K, value: Settings[K]) => save({ ...settings, [key]: value });

    return (
        <section className={classes.root}>
            <div className={classes.scroll}>
                <article className={mergeClasses(surfaces.card, classes.sectionCard)}>
                    <header className={classes.sectionHeader}>
                        <h2 className={classes.sectionTitle}>General</h2>
                        <p className={classes.sectionSubtitle}>Preferences saved locally on this device.</p>
                    </header>

                    <div className={classes.generalBody}>
                        <PreferenceRow label="Default view" description="How Team and Report cards are laid out by default.">
                            <SegmentedToggle<ViewMode>
                                value={preferences.defaultView}
                                onChange={setDefaultView}
                                options={[
                                    { value: 'grid', label: 'Grid', icon: <GridView className={classes.toggleIcon} aria-hidden /> },
                                    { value: 'list', label: 'List', icon: <ViewList className={classes.toggleIcon} aria-hidden /> },
                                ]}
                                ariaLabel="Default view"
                            />
                        </PreferenceRow>

                        <PreferenceRow label="Appearance" description="Switch between light and dark theme.">
                            <SegmentedToggle<ThemeMode>
                                value={preferences.themeMode}
                                onChange={setThemeMode}
                                options={[
                                    { value: 'light', label: 'Light', icon: <LightMode className={classes.toggleIcon} aria-hidden /> },
                                    { value: 'dark', label: 'Dark', icon: <DarkMode className={classes.toggleIcon} aria-hidden /> },
                                ]}
                                ariaLabel="Appearance"
                            />
                        </PreferenceRow>
                    </div>
                </article>

                <article className={mergeClasses(surfaces.card, classes.sectionCard)}>
                    <header className={classes.sectionHeader}>
                        <h2 className={classes.sectionTitle}>Azure DevOps</h2>
                        <p className={classes.sectionSubtitle}>Connection used to read and write your work items.</p>
                    </header>

                    <div className={classes.adoBody}>
                        <section className={classes.leftColumn}>
                            <SectionLabel text="Connection" />
                            <div className={classes.fields}>
                                <Field label="Personal Access Token (PAT)" required>
                                    <Input
                                        type={patVisible ? 'text' : 'password'}
                                        value={settings.pat}
                                        placeholder="your-pat-here"
                                        onChange={(_, data) => update('pat', data.value)}
                                        contentAfter={
                                            <button
                                                type="button"
                                                className={classes.eyeButton}
                                                aria-label={patVisible ? 'Hide PAT' : 'Show PAT'}
                                                onClick={() => setPatVisible((v) => !v)}
                                            >
                                                {patVisible ? (
                                                    <VisibilityOff className={classes.eyeIcon} />
                                                ) : (
                                                    <Visibility className={classes.eyeIcon} />
                                                )}
                                            </button>
                                        }
                                    />
                                </Field>

                                <Field label="Organisation" required>
                                    <Dropdown
                                        value={settings.org}
                                        selectedOptions={[settings.org]}
                                        onOptionSelect={(_, data) => {
                                            if (data.optionValue) {
                                                update('org', data.optionValue);
                                            }
                                        }}
                                    >
                                        {ORG_OPTIONS.map((o) => (
                                            <Option key={o} value={o}>
                                                {o}
                                            </Option>
                                        ))}
                                    </Dropdown>
                                </Field>

                                <Field label="Project" required>
                                    <Dropdown
                                        value={settings.project}
                                        selectedOptions={[settings.project]}
                                        onOptionSelect={(_, data) => {
                                            if (data.optionValue) {
                                                update('project', data.optionValue);
                                            }
                                        }}
                                    >
                                        {PROJECT_OPTIONS.map((p) => (
                                            <Option key={p} value={p}>
                                                {p}
                                            </Option>
                                        ))}
                                    </Dropdown>
                                </Field>
                            </div>
                        </section>

                        <aside className={classes.rightColumn}>
                            <SectionLabel text="How to get a PAT" />
                            <div className={classes.note}>
                                <span className={classes.noteBullet}>
                                    <Lightbulb className={classes.noteIcon} />
                                </span>
                                <div className={classes.noteLines}>
                                    <p className={classes.noteHeading}>Create a Personal Access Token in Azure DevOps</p>
                                    <ol className={classes.noteList}>
                                        <li>
                                            Open Azure DevOps → click your profile icon (top right) → <strong>User settings</strong> →{' '}
                                            <strong>Personal access tokens</strong>.
                                        </li>
                                        <li>
                                            Click <strong>+ New Token</strong>. Give it a name (e.g. <em>Time Tracker</em>) and pick an
                                            expiration date.
                                        </li>
                                        <li>
                                            Under <strong>Scopes</strong>, choose <strong>Custom defined</strong> and tick{' '}
                                            <strong>Work Items — Read &amp; Write</strong>. That&rsquo;s all this app needs.
                                        </li>
                                        <li>
                                            Click <strong>Create</strong>, copy the token (shown once), and paste it on the left.
                                        </li>
                                    </ol>
                                    <p className={classes.noteFootnote}>
                                        The token is stored only in your browser&rsquo;s local storage — never sent anywhere except Azure
                                        DevOps.
                                    </p>
                                </div>
                            </div>
                        </aside>
                    </div>
                </article>
            </div>
        </section>
    );
};

type PreferenceRowProps = { label: string; description: string; children: React.ReactNode };
const PreferenceRow: React.FC<PreferenceRowProps> = ({ label, description, children }) => {
    const classes = useStyles();
    return (
        <div className={classes.prefRow}>
            <div className={classes.prefText}>
                <span className={classes.prefLabel}>{label}</span>
                <span className={classes.prefDescription}>{description}</span>
            </div>
            <div className={classes.prefControl}>{children}</div>
        </div>
    );
};

type SegmentedOption<T extends string> = { value: T; label: string; icon: React.ReactNode };
type SegmentedToggleProps<T extends string> = {
    value: T;
    onChange: (next: T) => void;
    options: SegmentedOption<T>[];
    ariaLabel: string;
};
const SegmentedToggle = <T extends string>({ value, onChange, options, ariaLabel }: SegmentedToggleProps<T>) => {
    const classes = useStyles();
    return (
        <div className={classes.toggle} role="group" aria-label={ariaLabel}>
            {options.map((opt) => (
                <button
                    key={opt.value}
                    type="button"
                    aria-pressed={value === opt.value}
                    className={mergeClasses(classes.toggleBtn, value === opt.value && classes.toggleBtnActive)}
                    onClick={() => onChange(opt.value)}
                >
                    {opt.icon}
                    <span>{opt.label}</span>
                </button>
            ))}
        </div>
    );
};

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        minHeight: 0,
    },
    scroll: {
        flexGrow: 1,
        overflowY: 'auto',
        minHeight: 0,
        padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL} ${tokens.spacingVerticalL}`,
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalL,
    },
    sectionCard: {
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalL,
    },
    sectionHeader: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    sectionTitle: {
        margin: 0,
        fontSize: '17px',
        fontWeight: tokens.fontWeightBold,
        color: tokens.colorNeutralForeground1,
    },
    sectionSubtitle: {
        margin: 0,
        fontSize: '13px',
        color: palette.dimText,
    },

    adoBody: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
        alignItems: 'stretch',
    },
    leftColumn: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        paddingRight: tokens.spacingHorizontalXXL,
    },
    rightColumn: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        paddingLeft: tokens.spacingHorizontalXXL,
        borderLeft: `1px solid ${palette.cardBorder}`,
    },
    fields: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalM,
    },
    note: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: tokens.spacingHorizontalS,
        padding: tokens.spacingHorizontalM,
        backgroundColor: palette.panelMuted,
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: tokens.borderRadiusLarge,
    },
    noteBullet: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '18px',
        height: '18px',
        borderRadius: '50%',
        backgroundColor: palette.accentBg,
        flexShrink: 0,
        marginTop: '2px',
    },
    noteIcon: {
        width: '12px',
        height: '12px',
        color: palette.accentText,
    },
    noteLines: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        minWidth: 0,
    },
    noteHeading: {
        margin: 0,
        fontSize: '12.5px',
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
    },
    noteList: {
        margin: 0,
        paddingLeft: '18px',
        display: 'flex',
        flexDirection: 'column',
        gap: '3px',
        fontSize: '12px',
        color: tokens.colorNeutralForeground2,
        lineHeight: 1.45,
    },
    noteFootnote: {
        margin: 0,
        marginTop: '4px',
        fontSize: '11.5px',
        color: tokens.colorNeutralForeground3,
        fontStyle: 'italic',
    },
    eyeButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '28px',
        height: '28px',
        background: 'transparent',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        color: palette.dimText,
        '&:hover': {
            color: tokens.colorNeutralForeground1,
        },
    },
    eyeIcon: {
        width: '18px',
        height: '18px',
    },

    generalBody: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    prefRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: tokens.spacingHorizontalL,
        padding: `${tokens.spacingVerticalM} 0`,
        borderBottom: `1px solid ${palette.cardBorder}`,
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    prefText: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        minWidth: 0,
    },
    prefLabel: {
        fontSize: '14px',
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
    },
    prefDescription: {
        fontSize: '13px',
        color: palette.dimText,
    },
    prefControl: {
        flexShrink: 0,
    },

    toggle: {
        display: 'inline-flex',
        alignItems: 'stretch',
        width: '170px',
        height: '36px',
        backgroundColor: palette.panelBg,
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: palette.pillRadius,
        boxShadow: palette.softShadowSm,
        overflow: 'hidden',
    },
    toggleBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        flex: 1,
        border: 'none',
        borderLeft: `1px solid ${palette.cardBorder}`,
        background: 'transparent',
        padding: '0 10px',
        fontFamily: 'inherit',
        fontSize: '12.5px',
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground2,
        cursor: 'pointer',
        transition: 'background-color 160ms ease, color 160ms ease',
        '&:first-child': {
            borderLeft: 'none',
        },
        '&:hover': {
            backgroundColor: palette.accentBg,
            color: palette.accentText,
        },
        '&:focus-visible': {
            outline: `2px solid ${palette.accent}`,
            outlineOffset: '-2px',
        },
    },
    toggleBtnActive: {
        backgroundColor: palette.accent,
        color: palette.primaryText,
        '&:hover': {
            backgroundColor: palette.accentHover,
            color: palette.primaryText,
        },
    },
    toggleIcon: {
        width: '16px',
        height: '16px',
    },
});
